import * as React from "react";
import { FormProvider } from "react-hook-form";

import { useAuth } from "@/providers";
import {
  ResourceFormActions,
  submitError,
  type SubmitError,
} from "@/shared/components/ResourceForm";
import {
  type AgentRecord,
  type LaunchAgentRequest,
} from "@/shared/domain/agents";
import { type LaunchSeed } from "@/shared/domain/agents/launchSubject";

import { parseSavedAgent } from "../Agents/buildRequestTemplate";

import styles from "./LaunchAgentForm.module.css";
import { type LaunchFormOutput } from "./launchFormSchema";
import { AdvancedSection } from "./sections/AdvancedSection";
import { VariablesSection } from "./sections/VariablesSection";
import { VisibilitySection } from "./sections/VisibilitySection";
import { useLaunchForm } from "./useLaunchForm";

export interface LaunchAgentFormProps {
  agent: AgentRecord;
  /** The ids the launch link carried. */
  seed: LaunchSeed;
  /**
   * Receives the built request and resolves when the page's launch settles.
   * The pending promise is the submitting state and a rejection becomes the
   * form's own server error, so neither is a separate prop.
   */
  onSubmit: (request: LaunchAgentRequest) => Promise<void>;
  onCancel: () => void;
}

/** The wire payload; a window left empty sends no `analysis_scope` at all. */
function buildLaunchRequest(values: LaunchFormOutput): LaunchAgentRequest {
  const request: LaunchAgentRequest = {
    values: values.values,
    visibility: values.visibility,
  };
  if (values.analysisScope !== null) {
    request.analysis_scope = values.analysisScope;
  }
  return request;
}

/**
 * The launch form: what the agent starts with, who can read the thread, and
 * the settings most launches leave alone. Mount it once the agent has loaded;
 * its fields and required checks come from the agent's variables.
 */
export function LaunchAgentForm({
  agent,
  seed,
  onSubmit,
  onCancel,
}: LaunchAgentFormProps) {
  const form = useLaunchForm({ agent, seed });
  const { currentOrganization } = useAuth();
  const kickoffMessage = React.useMemo(
    () => parseSavedAgent(agent).kickoffMessage,
    [agent],
  );

  // `handleSubmit` re-throws the handler's rejection, so the page's refusal is
  // caught around the call.
  const submit = form.handleSubmit(async (values) => {
    await onSubmit(buildLaunchRequest(values));
  });

  // Not the form's `root` error: every change after the first submit
  // re-validates the whole form, which would wipe it.
  const [serverError, setServerError] = React.useState<SubmitError | null>(
    null,
  );

  return (
    <FormProvider {...form}>
      <form
        className={styles.form}
        data-testid="launch-agent-form"
        onSubmit={(event) => {
          setServerError(null);
          submit(event).catch((error: unknown) => {
            setServerError(submitError(error, "create", "thread"));
          });
        }}
        noValidate
      >
        <VariablesSection
          variables={agent.variables}
          kickoffMessage={kickoffMessage}
        />
        <VisibilitySection orgName={currentOrganization?.name} />
        <AdvancedSection />
        <ResourceFormActions
          submitLabel="Launch"
          idleHint=""
          submitKind="launch"
          onCancel={onCancel}
          serverError={serverError}
        />
      </form>
    </FormProvider>
  );
}
